import React, { useMemo } from 'react'
import { ConversationContent } from 'components/inbox/conversation_content/ConversationContent'
import { ConversationMessageThread } from 'components/inbox/shared/ConversationMessageThread'
import { formatReviewerRecipientName } from 'components/inbox/utils'
import type { InboxConversationDetail, InboxMessage, InboxParticipant } from 'components/inbox/inboxTypes'
import type { Review as ReviewType } from 'components/supplier_show/prop_shapes/review'

export interface IssueContext {
  issueId: number
  issueName: string
}

export interface MessageSubjectReview {
  id: number
  shareWithSupplier: boolean
  isAnonymous: boolean
  author: {
    name: string
    organization: string | null
    thumbnail: string
  }
}

export const buildMessageSubjectReview = (review: ReviewType): MessageSubjectReview => ({
  id: review.id,
  shareWithSupplier: review.share_with_supplier,
  isAnonymous: review.is_anonymous === true,
  author: {
    name: review.author.name,
    organization: review.author.organization ?? null,
    thumbnail: review.author.thumbnail
  }
})

export const useMessageSubjectReview = (review: ReviewType): MessageSubjectReview => {
  const reviewAuthorOrganization = review.author.organization ?? null

  return useMemo(() => buildMessageSubjectReview(review), [
    review.id,
    review.share_with_supplier,
    review.is_anonymous,
    review.author.name,
    reviewAuthorOrganization,
    review.author.thumbnail
  ])
}

export const buildReviewerRecipient = (review: MessageSubjectReview): InboxParticipant[] => {
  const reviewerFullName = formatReviewerRecipientName(review.author.name, review.isAnonymous)

  return [{
    firstName: reviewerFullName,
    fullName: reviewerFullName,
    display: review.isAnonymous ? 'anonymous' : 'full',
    orgName: review.author.organization,
    avatarUrl: review.author.thumbnail
  }]
}

export const renderMessageThread = (existingMessages: InboxMessage[]): React.ReactNode => (
  <ConversationMessageThread messages={existingMessages} />
)

export const renderIssueConversationContent = (
  conversationDetail: InboxConversationDetail | null,
  existingMessages: InboxMessage[],
  onActiveIssueClick?: () => void
): React.ReactNode => (
  conversationDetail == null
    ? renderMessageThread(existingMessages)
    : <ConversationContent conversation={conversationDetail} onActiveIssueClick={onActiveIssueClick} />
)

export const appendMessageToConversation = (
  conversation: InboxConversationDetail | null,
  message: InboxMessage
): InboxConversationDetail | null => {
  if (conversation == null) return conversation

  return {
    ...conversation,
    messages: [...conversation.messages, message]
  }
}
